import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from 'src/hooks/useApi';
import LoadingSpinner from 'src/components/LoadingSpinner';
import { useNavigate, useLocation } from 'react-router';
import { ArtifactDetailModal } from 'src/components/ArtifactDetail/components/ArtifactDetailModal';
import './artifact-detail-overrides.scss';
import { GetArtifactDetailApiType } from 'src/types/useApi';
import { useRenderContent } from 'src/components/ArtifactDetail/components/useRenderContent';

type ArtifactDetailProps = {
    artifactId: string;
    contentObjectLcmsXmlGUID: string;
};

const ArtifactDetail = ({
    artifactId,
    contentObjectLcmsXmlGUID,
}: ArtifactDetailProps) => {
    const navigate = useNavigate();
    const location = useLocation();

    // Extract xref GUID from URL hash if present (format: #/topicGuid/topicName/xref/xrefGuid)
    const getXrefFromHash = useCallback(() => {
        const hash = location.hash;
        const xrefMatch = hash.match(/\/xref\/([^/?]+)/);
        return xrefMatch ? `${xrefMatch[1]}_xref.html` : '';
    }, [location.hash]);

    const [modalHref, setModalHref] = useState<string>(getXrefFromHash());

    // Update modalHref when URL changes (e.g., direct navigation to xref URL)
    useEffect(() => {
        const xrefFromHash = getXrefFromHash();
        if (xrefFromHash && !modalHref) {
            setModalHref(xrefFromHash);
        }
    }, [location.hash, getXrefFromHash]);

    // Update URL when modal opens/closes
    const handleSetModalHref = useCallback(
        (href: string) => {
            setModalHref(href);

            const currentHash = location.hash;
            // Remove any existing xref from the hash
            const baseHash = currentHash.replace(/\/xref\/[^/?]+/, '');

            if (href) {
                // Extract the xref GUID from href (format: guid_xref.html)
                const xrefGuid = href.replace(/_xref\.html$/, '');
                const newHash = `${baseHash}/xref/${xrefGuid}`;
                window.history.replaceState(null, '', newHash);
            } else {
                // Modal closed, remove xref from URL
                window.history.replaceState(null, '', baseHash);
            }
        },
        [location.hash]
    );

    const {
        makeRequest: getDocumentHTML,
        data,
        error,
        loading,
    }: GetArtifactDetailApiType = useApi({
        method: 'GET',
    });

    useEffect(() => {
        if (error) {
            navigate('/error', {
                state: { ticketUrl: (error as any)?.ticketUrl },
            });
        }
    }, [error]);

    useEffect(() => {
        if (artifactId && contentObjectLcmsXmlGUID) {
            getDocumentHTML({
                url: `/learning-artifacts/v2/document/${artifactId}/${contentObjectLcmsXmlGUID}`,
            });
        }
    }, [artifactId, contentObjectLcmsXmlGUID]);

    const { artifactHtml } = useRenderContent({
        data,
        artifactId,
        setModalHref: handleSetModalHref,
        loading,
    });
    return loading || artifactId === '' ? (
        <LoadingSpinner />
    ) : (
        <>
            {modalHref && (
                <ArtifactDetailModal
                    modalHref={modalHref}
                    setModalHref={handleSetModalHref}
                    lcmsXMLGUID={contentObjectLcmsXmlGUID}
                    artifactId={artifactId}
                />
            )}
            <object className={'xyleme-container'}>{artifactHtml}</object>
        </>
    );
};

export default ArtifactDetail;
